import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from '../../lib/axios';
import { useLang, LanguageBar } from '../../i18n/cda';
import { CdaLogo } from '../../components/cda/CdaBrand';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Textarea } from '../../components/ui/textarea';
import { Checkbox } from '../../components/ui/checkbox';
import { toast } from 'sonner';
import { HeartHandshake, CheckCircle, ShieldCheck, ArrowLeft } from 'lucide-react';

const GUIDELINES = [
  "Hire and pay a CDA candidate while they complete their required 480 observation/clinical hours.",
  "Allow the candidate's CDA Verification Visit (observation) to take place at your center.",
  "Have your center director approve and verify the candidate on the CDA Council (YourCouncil) portal.",
];

const CenterPartner = () => {
  const navigate = useNavigate();
  const { t } = useLang();
  const [form, setForm] = useState({
    center_name: '', contact_name: '', role: '', email: '', phone: '', city: '', age_groups: '', notes: '',
  });
  const [agreed, setAgreed] = useState(false);
  const [loading, setLoading] = useState(false);
  const [done, setDone] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    if (!agreed) { toast.error(t('Please agree to the sharing guidelines to continue.')); return; }
    setLoading(true);
    try {
      await axios.post('/cda/center-partner', { ...form, agreed: true });
      setDone(true);
      toast.success(t('Thank you! Your center has been added to our network.'));
    } catch (err) {
      toast.error(err.response?.data?.detail || t('Something went wrong. Please try again.'));
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-[#fbf8f3]">
      <LanguageBar />
      <header className="px-5 sm:px-10 py-4 flex items-center justify-between">
        <CdaLogo size={36} />
        <button onClick={() => navigate('/cda')} className="flex items-center gap-1.5 text-sm text-[#0d7d6f] hover:underline" data-testid="centers-back">
          <ArrowLeft className="w-4 h-4" /> {t('Back')}
        </button>
      </header>

      <main className="max-w-2xl mx-auto px-5 sm:px-8 py-6">
        <div className="text-center mb-8">
          <div className="w-14 h-14 rounded-2xl bg-[#0d7d6f]/10 flex items-center justify-center mx-auto mb-4">
            <HeartHandshake className="w-7 h-7 text-[#0d7d6f]" />
          </div>
          <h1 className="text-3xl sm:text-4xl font-bold text-slate-900 mb-3" style={{ fontFamily: 'Manrope, sans-serif' }}>
            {t('Partner with Care Comply')}
          </h1>
          <p className="text-slate-600">
            {t('Are you a Texas learning center? Join our network to be connected with motivated CDA candidates in your area who are seeking a paid placement for their 480 observation/clinical hours.')}
          </p>
        </div>

        {done ? (
          <div className="bg-white rounded-2xl border border-emerald-200 p-8 text-center" data-testid="center-success">
            <CheckCircle className="w-12 h-12 text-[#0d7d6f] mx-auto mb-3" />
            <h2 className="text-xl font-bold text-slate-900 mb-2">{t('You\'re on the list! 🎉')}</h2>
            <p className="text-slate-600 mb-6">{t('Care Comply will reach out and connect you with CDA candidates near you.')}</p>
            <Button onClick={() => navigate('/cda')} className="bg-[#0d7d6f] hover:bg-[#0a6358]" data-testid="center-done-button">{t('Back to home')}</Button>
          </div>
        ) : (
          <form onSubmit={submit} className="bg-white rounded-2xl border border-slate-100 p-6 sm:p-8 space-y-4 shadow-sm">
            <div className="grid sm:grid-cols-2 gap-4">
              <div>
                <Label>{t('Center / program name')} *</Label>
                <Input value={form.center_name} onChange={(e) => setForm({ ...form, center_name: e.target.value })} required className="mt-1.5 h-11" data-testid="center-name" />
              </div>
              <div>
                <Label>{t('City or ZIP code')} *</Label>
                <Input value={form.city} onChange={(e) => setForm({ ...form, city: e.target.value })} required className="mt-1.5 h-11" placeholder="San Antonio, 78212" data-testid="center-city" />
              </div>
              <div>
                <Label>{t('Your name')}</Label>
                <Input value={form.contact_name} onChange={(e) => setForm({ ...form, contact_name: e.target.value })} className="mt-1.5 h-11" data-testid="center-contact" />
              </div>
              <div>
                <Label>{t('Your role')}</Label>
                <Input value={form.role} onChange={(e) => setForm({ ...form, role: e.target.value })} className="mt-1.5 h-11" placeholder={t('e.g., Director')} data-testid="center-role" />
              </div>
              <div>
                <Label>{t('Email')} *</Label>
                <Input type="email" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} required className="mt-1.5 h-11" data-testid="center-email" />
              </div>
              <div>
                <Label>{t('Phone')}</Label>
                <Input value={form.phone} onChange={(e) => setForm({ ...form, phone: e.target.value })} className="mt-1.5 h-11" data-testid="center-phone" />
              </div>
            </div>
            <div>
              <Label>{t('Age groups you serve')}</Label>
              <Input value={form.age_groups} onChange={(e) => setForm({ ...form, age_groups: e.target.value })} className="mt-1.5 h-11" placeholder={t('Infants, toddlers, preschool...')} data-testid="center-ages" />
            </div>
            <div>
              <Label>{t('Anything else? (optional)')}</Label>
              <Textarea value={form.notes} onChange={(e) => setForm({ ...form, notes: e.target.value })} className="mt-1.5" data-testid="center-notes" />
            </div>

            {/* Privacy & sharing guidelines */}
            <div className="bg-[#0d7d6f]/5 border border-[#0d7d6f]/20 rounded-xl p-4">
              <p className="font-semibold text-slate-900 flex items-center gap-2 mb-2"><ShieldCheck className="w-5 h-5 text-[#0d7d6f]" /> {t('Privacy & sharing guidelines')}</p>
              <p className="text-sm text-slate-600 mb-3">
                {t('To make the connection, your center\'s details will be shared with matched CDA candidates, and candidate details will be shared with your center. We do not sell your information. By joining, your center indicates it is willing to:')}
              </p>
              <ul className="space-y-1.5">
                {GUIDELINES.map((g) => (
                  <li key={g} className="flex items-start gap-2 text-sm text-slate-700">
                    <CheckCircle className="w-4 h-4 text-[#0d7d6f] shrink-0 mt-0.5" /> {t(g)}
                  </li>
                ))}
              </ul>
            </div>

            <label className="flex items-start gap-2 text-sm text-slate-700">
              <Checkbox className="mt-0.5" checked={agreed} onCheckedChange={(c) => setAgreed(!!c)} data-testid="center-agree" />
              <span>{t('I have read and agree to the privacy & sharing guidelines above.')}</span>
            </label>

            <Button type="submit" disabled={loading} className="w-full h-11 rounded-full bg-[#0d7d6f] hover:bg-[#0a6358]" data-testid="center-submit">
              {loading ? t('Please wait...') : t('Join the network')}
            </Button>
          </form>
        )}
      </main>
      <footer className="py-8 text-center text-sm text-slate-500">
        {t('Care Comply is a service of Barro Roots Contracting. • CDA® is a trademark of the Council for Professional Recognition.')}
      </footer>
    </div>
  );
};

export default CenterPartner;
